<template>
  <div>
    <div class="flex flex-row h-full">
      <div id="product_facets"
           :class="{'w-1/2 lg:w-2/5 1.5xl:w-1/4 4k:w-1/5': opened, 'w-1/30':!opened}">
        <c-decor-facets
          v-if="facetsLoaded"
          class="overflow-y-auto h-full"
          @reload-facets="resetFacets"
          :facets="facets"
          :query-data="queryObject"
          :opened.sync="opened"
          :only-new.sync="onlyNew"
          :only-top.sync="onlyTop"/>
      </div>
      <div id="decors" class="h-full"
           :class="{'w-1/2 lg:w-3/5 1.5xl:w-3/4 pl-10 pr-10 1.5xl:pl-10 1.5xl:pr-20 4k:w-4/5': opened, 'w-29/30 px-2':!opened}">
        <div
          class="flex flex-row justify-between w-full gap-1 h-3/20 lg:h-1/20 mb-5 mr-4 items-center">
          <div class="flex flex-row gap-2">
            <c-selection-picker
              v-if="displayQuick && hasPermissionToSeeQuickSelections()"
              class="col-span-2 1.5xl:col-span-1 selection"
              :selected.sync="selectedSelection"
              :all-selected="allAdded"
              @addSearchProductsToSelection="addSearchDecorsToSelection">
            </c-selection-picker>
            <div class="col-span-1 flex items-center" v-if="hasPermissionToSeeQuickSelections()">
              <div @click="displayQuick=!displayQuick" class="cursor-pointer displayQuick">
                <div v-if="displayQuick" class="displayQuick bg-classen__primary">
                  <label><i class="fas fa-eye"></i></label>
                </div>
                <div v-if="!displayQuick" class="displayQuick bg-classen__primary">
                  <label><i class="fas fa-eye-slash"></i></label>
                </div>
              </div>
              <p v-if="!displayQuick" class="ml-2 font-bold"> Show quickselections </p>
            </div>
          </div>
        </div>
        <c-decor-display
          v-if="isLoaded"
          class="overflow-y-auto h-18/20 pb-5"
          :filter-open="opened"
          :decors="filteredDecors"
          :hasMore="links.next !== null && meta.total !== 0 && filteredDecors.length !== 0"
          :selectedSelection="selectedSelection"
          @load-more="fetchNextDecors"
          :displayQuick="!!(displayQuick && hasPermissionToSeeQuickSelections())"
        />
        <div class="flex align-middle h-17/20" v-else>
          <c-loader :inline="true"/>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import debounce from "debounce";
import DecorFacets from "./DecorFacets";
import DecorsDisplay from "./DecorsDisplay";
import getTranslation from "../../../mixins/getTranslation";
import SortingDropdown from "../SortingDropdown";
import SelectionPicker from "../SelectionPicker";
import EventBus from "../../../services/EventBus";
import getPermissions from "../../../mixins/getPermissions";
import Loader from "../../Loader";

export default {
  name: 'Decors',

  mixins: [getTranslation, getPermissions],

  components: {
    'c-decor-facets': DecorFacets,
    'c-decor-display': DecorsDisplay,
    'c-sort': SortingDropdown,
    'c-selection-picker': SelectionPicker,
    'c-loader': Loader,
  },

  data() {
    return {
      page: 1,
      size: 48,
      meta: [],
      links: [],
      decors: [],
      opened: true,
      onlyNew: false,
      isFetching: false,
      facetsLoaded: true,
      displayQuick: true,
      onlyTop: false,
      isLoaded: false,
      facets: {},
      allAdded: false,
      selectedSelection: {id: -1, name: "Set default selection"},
      queryObject: {
        query: {
          decorName: "",
          decorNumber: null,
          categories: [],
          plankTypes: [],
          plankColors: [],
          materials: [],
          decorTypes: [],
          isActive: true,
        },
        sort: {},
        language: "en"
      },
    }
  },

  created() {
    this.fetchFacets();
    this.fetchDecors();

    EventBus.listen('update-isActive-decors', this.updateIsActive)
  },

  destroyed() {
    EventBus.destroy('update-isActive-decors', this.updateIsActive)
  },

  computed: {
    filters() {
      return [
        {value: 'newToOld', direction: 'desc', text: this.getTranslation('newToOld')},
        {value: 'decorName', direction: 'asc', text: this.getTranslation('name')},
        {value: 'decorNumber', direction: 'desc', text: this.getTranslation('decorNumber')},
        {value: 'topFirst', direction: 'desc', text: this.getTranslation('topDecorsFirst')},
      ];
    },

    filteredDecors() {
      if (this.onlyNew) {
        return this.decors.filter((decor) => decor.isNew);
      }

      if (this.onlyTop) {
        return this.decors.filter((decor) => decor.isTop);
      }

      return this.decors;
    },

    selectedCategories() {
      if (!this.facets.categories) {
        return []
      }

      const categories = this.facets.categories.filter((item) => {
        return item.selected;
      });

      this.queryObject.query.categories = categories.map((category) => category.name);
      return categories
    },

    selectedPlankTypes() {
      if (!this.facets.plankTypes) {
        return []
      }

      const plankTypes = this.facets.plankTypes.filter((item) => {
        return item.selected;
      });

      this.queryObject.query.plankTypes = plankTypes.map((type) => type.name);
      return plankTypes
    },

    selectedDecorTypes() {
      if (!this.facets.decorTypes) {
        return []
      }

      const decorTypes = this.facets.decorTypes.filter((item) => {
        return item.selected;
      });

      this.queryObject.query.decorTypes = decorTypes.map((type) => type.name);
      return decorTypes
    },

    selectedPlankColors() {
      if (!this.facets.plankColors) {
        return []
      }

      const plankColors = this.facets.plankColors.filter((item) => {
        return item.selected;
      });

      this.queryObject.query.plankColors = plankColors.map((color) => color.name);
      return plankColors
    },

    selectedMaterials() {
      if (!this.facets.materials) {
        return []
      }

      const materials = this.facets.materials.filter((item) => {
        return item.selected;
      });

      this.queryObject.query.materials = materials.map((material) => material.name);
      return materials
    },
  },

  watch: {
    selectedSelection() {
      this.allAdded = false;
    },

    selectedCategories() {
      if (this.selectedCategories.length === 0 && !this.isLoaded) {
        return;
      }

      this.fetchDecors();
    },

    selectedPlankTypes() {
      if (this.selectedPlankTypes.length === 0 && !this.isLoaded) {
        return;
      }

      this.fetchDecors();
    },

    selectedDecorTypes() {
      if (this.selectedDecorTypes.length === 0 && !this.isLoaded) {
        return;
      }

      this.fetchDecors();
    },

    selectedPlankColors() {
      if (this.selectedPlankColors.length === 0 && !this.isLoaded) {
        return;
      }

      this.fetchDecors();
    },

    selectedMaterials() {
      if (this.selectedMaterials.length === 0 && !this.isLoaded) {
        return;
      }

      this.fetchDecors();
    },

    "queryObject.query.decorName": debounce(function () {
      this.fetchDecors();
    }, 500),

    "queryObject.query.decorNumber": debounce(function () {
      this.fetchDecors();
    }, 500),

    "queryObject.sort": debounce(function () {
      this.fetchDecors();
    }, 500),
  },

  methods: {
    rerenderDisplay() {
      this.isLoaded = false;
      this.$nextTick(() => {
        this.isLoaded = true;
      });
    },

    updateIsActive(status) {
      this.queryObject.query.isActive = status;
      this.fetchDecors();
    },

    updateSort(val) {
      this.queryObject.sort = {};
      this.queryObject.sort[val.value] = {order: val.direction}
    },

    fetchNextDecors() {
      this.page++;
      const data = {page: this.page, size: this.size, queryData: this.queryObject}
      this.$dashboardStore.dispatch('searchDecors', data)
        .then((response) => {
          this.decors = [...this.decors, ...response.data];
          this.meta = response.meta;
          this.links = response.links;
        });
    },

    async fetchDecors() {
      if (this.isFetching) {
        return
      }
      this.isLoaded = false;
      this.isFetching = true;

      this.page = 1;
      const data = {page: this.page, size: this.size, queryData: this.queryObject}
      const response = await this.$dashboardStore.dispatch('searchDecors', data)

      this.decors = response.data;
      this.meta = response.meta;
      this.links = response.links;
      this.rerenderDisplay()
      this.isFetching = false;
      this.isLoaded = true;
      this.$dashboardStore.dispatch('getSelections');
    },

    fetchFacets() {
      this.$dashboardStore.dispatch('getDecorFacets')
        .then((response) => {
          this.facets = response.data;
        });
    },

    resetFacets() {
      this.queryObject.query = {
        decorName: "",
        decorNumber: null,
        categories: [],
        plankTypes: [],
        plankColors: [],
        materials: [],
        decorTypes: []
      }
      this.$set(this, 'onlyNew', false)
      this.$set(this, 'onlyTop', false)
      this.facetsLoaded = false;
      this.$nextTick(() => {
        this.facetsLoaded = true
        this.fetchFacets();
      })
    },

    addSearchDecorsToSelection() {
      if (this.selectedSelection.id === -1) {
        return;
      }

      let data = this.queryObject;
      data.selectionId = this.selectedSelection.id

      EventBus.fire('showLoader');
      if (this.allAdded) {
        this.$dashboardStore.dispatch('removeSearchDecorFromSelection', data)
          .then(() => {
            this.decors.forEach((decor) => {
              decor.selections = decor.selections.filter((selectionItem) => {
                return selectionItem.id !== this.selectedSelection.id;
              });
            })
            this.allAdded = false;
            EventBus.fire('hideLoader');
          })
          .catch((error) => {
            console.warn('something went wrong adding all', error);
            EventBus.fire('hideLoader');
          });
        return;
      }

      this.$dashboardStore.dispatch('addSearchDecorsToSelection', data)
        .then(() => {
          this.decors.forEach((decor) => {
            decor.selections.push(this.selectedSelection);
          })
          this.allAdded = true;
          EventBus.fire('hideLoader');
        })
        .catch((error) => {
          console.warn('something went wrong adding all', error);
          EventBus.fire('hideLoader');
        });
    },
  }
};
</script>

<style scoped>
.displayQuick {
  width: 40px;
  height: 40px;
  border-radius: 20px;
  line-height: 40px;
  color: white;
}

.selection {
  width: 250px;
}

.order_by {
  width: 250px;
}
</style>
